(ns hai.notes.form
  "The note-editing pieces shared by the note detail page and the home-page
   notes mini-app: [[note-form]], the auto-saving text field on the form
   system, and [[note-extras]], the tags/links sections with their open/close
   state and tag search wired in. Hosts render them as siblings — the extras
   stay outside the form because the form system freezes its render at setup,
   which would freeze live tag/link values passed through it."
  (:require
   [hai.notes.link-combobox :as link-combobox]
   [hai.notes.sections :as sections]
   [hai.tags.combobox :as tag-combobox]
   [hai.ui.components.form.core :as form]
   [hai.ui.rf-sugar :refer [<s]]
   [reagent.core :as r]))

(def ^:private save-debounce-ms 750)

(def note-form-schema
  [:map [:note/text :string]])

(defn- note-form-render
  [{:form/keys [fields]}
   {:keys [editing? placeholder auto-focus? !textarea on-save-text !autosave]}]
  (r/with-let [!pending-text (atom nil)
               !timer (atom nil)
               cancel-timer! (fn []
                               (when-let [id @!timer]
                                 (js/clearTimeout id)
                                 (reset! !timer nil)))
               save-pending! (fn []
                               (when-let [text @!pending-text]
                                 (reset! !pending-text nil)
                                 (on-save-text text)))
               flush! (fn []
                        (cancel-timer!)
                        (save-pending!))
               discard! (fn []
                          (cancel-timer!)
                          (reset! !pending-text nil))
               schedule! (fn []
                           (cancel-timer!)
                           (reset! !timer
                                   (js/setTimeout
                                    (fn []
                                      (reset! !timer nil)
                                      (save-pending!))
                                    save-debounce-ms)))
               _ (when !autosave (reset! !autosave {:discard! discard!}))]
    (let [{text-field :note/text} fields
          text-val (:form.field/current-value text-field)
          set-text! (:form.field/set-value! text-field)]
      (if editing?
        [:textarea.body
         (cond-> {:default-value (or text-val "")
                  :aria-label "Edit note"
                  :on-change (fn [e]
                               (let [value (.. e -target -value)]
                                 (reset! !pending-text value)
                                 (set-text! value)
                                 (schedule!)))
                  :on-blur (fn [_] (flush!))}
           placeholder (assoc :placeholder placeholder)
           auto-focus? (assoc :auto-focus true)
           !textarea (assoc :ref #(reset! !textarea %)))]
        [:div.body (or text-val "")]))
    (finally
      (flush!))))

(defn note-form
  "The auto-saving note text field: `[:textarea.body …]` (or `[:div.body …]`
   when not `:editing?`) for the host to style. `:note` seeds the text;
   `:!textarea` receives the DOM node so the host can focus it. `:!autosave`
   receives `{:discard! f}` for dropping the pending debounced save — for hosts
   that file the text through another path (archiving) and must keep the
   unmount flush from re-saving it. `:resume-text?` makes a mount pick up the
   form state's live text over `:note` — for hosts whose pad and full-screen
   editor share a form-id, so switching hands the text over without waiting
   out the save round-trip."
  [{:keys [form-id note resume-text?] :as props}]
  (let [form-text (when resume-text?
                    @(<s [:form.field/current-value form-id :note/text]))]
    [form/component
     {:id form-id
      :schema note-form-schema
      :initial-value {:note/text (or form-text (:note/text note) "")}
      :render (fn [form-state]
                ^{:key (:form/setup-id form-state)}
                [note-form-render form-state props])}]))

(defn note-extras
  "The tags/links sections with their open/close state and the tag search
   wired in; render next to [[note-form]]. Values are plain props — deref
   them in the host's render. The open-section state is internal by default;
   a host may pass its own `:open-set` / `:open-section!` / `:close-section!`.
   Adding a tag or link collapses its section — the adder hides, reopened by
   its ＋ control."
  [{:keys [editing? tags link-urls contacts linked-notes linked-tasks note-uuid
           open-set open-section! close-section!
           on-add-tag on-remove-tag on-add-link on-remove-link
           on-add-contact on-remove-contact on-add-note on-remove-note
           on-add-task on-remove-task on-open-entity]}]
  (r/with-let [!open-sections (r/atom #{})]
    (let [open!  (or open-section!  (fn [section] (swap! !open-sections conj section)))
          close! (or close-section! (fn [section] (swap! !open-sections disj section)))]
      [sections/note-extras
       {:editing?             editing?
        :tags                 tags
        :link-urls            link-urls
        :contacts             contacts
        :linked-notes         linked-notes
        :linked-tasks         linked-tasks
        :note-uuid            note-uuid
        :open-set             (if (some? open-set) open-set @!open-sections)
        :open-section!        open!
        :close-section!       close!
        :on-add-tag           (fn [tag] (on-add-tag tag) (close! :tags))
        :on-remove-tag        on-remove-tag
        :on-add-link          (fn [link-url] (on-add-link link-url) (close! :links))
        :on-remove-link       on-remove-link
        ;; Hosts deal in uuids, not entity maps — the picker's shape stops here.
        :on-add-contact       (when on-add-contact
                                (fn [contact] (on-add-contact (:contact/uuid contact))))
        :on-remove-contact    (when on-remove-contact
                                (fn [contact] (on-remove-contact (:contact/uuid contact))))
        :on-add-note          (when on-add-note
                                (fn [note] (on-add-note (:note/uuid note))))
        :on-remove-note       (when on-remove-note
                                (fn [note] (on-remove-note (:note/uuid note))))
        :on-add-task          (when on-add-task
                                (fn [task] (on-add-task (:task/uuid task))))
        :on-remove-task       (when on-remove-task
                                (fn [task] (on-remove-task (:task/uuid task))))
        :on-open-entity       on-open-entity
        :render-tag-search    tag-combobox/search-slot
        ;; One picker covers all three families, so any of the add handlers
        ;; being wired means the host wants the searchable adder.
        :render-entity-search (when (or on-add-contact on-add-note on-add-task)
                                link-combobox/entity-search)}])))
